import { memo, useState } from 'react';
import { IAudienceManager } from './types';
import { useModalState } from 'hooks/useModalState';
import { IAudienceMembers, TConvertedAudience, TSelectedSourcedSpecificUser } from '../types';
import { SelectAudienceButton } from '../select-audience-button';
import { AudienceSelectionDialog } from '../audience-selection-dialog';
import { AudienceMembersDetailsDialog } from '../audience-members-details-dialog';
import { CustomAudienceSetupDialog } from '../custom-audience-setup-dialog';
import { DeleteAudienceDialog } from '../delete-audience-dialog';

export const AudienceManager = memo(
  ({
    postAudience,
    projectAudiences,
    projectProfiles,
    projectId,
    onSetLatestPostAudience,
  }: IAudienceManager) => {
    const [activeAudience, setActiveAudience] = useState<TConvertedAudience>(postAudience);

    const [
      isAudienceSelectionDialogOpen,
      openAudienceSelectionDialog,
      closeAudienceSelectionDialog,
    ] = useModalState();
    const [isCustomAudienceDialogOpen, openCustomAudienceDialog, closeCustomAudienceDialog] =
      useModalState();
    const [selectedAudienceMembers, setSelectedAudienceMembers] = useState<IAudienceMembers | null>(
      null
    );
    const [audienceToDelete, setAudienceToDelete] = useState<TSelectedSourcedSpecificUser | null>(
      null
    );

    const handleAudienceSave = () => {
      onSetLatestPostAudience(activeAudience);
      closeAudienceSelectionDialog();
    };

    const resetAudienceSelection = () => {
      setActiveAudience(postAudience);
      closeAudienceSelectionDialog();
    };

    return (
      <>
        <SelectAudienceButton
          postAudience={postAudience}
          profilesCount={projectProfiles?.length}
          onClick={openAudienceSelectionDialog}
        />
        {isAudienceSelectionDialogOpen && (
          <AudienceSelectionDialog
            projectId={projectId}
            activeAudience={activeAudience}
            availableAudiences={projectAudiences}
            availableProfiles={projectProfiles}
            onSetAudienceMembers={setSelectedAudienceMembers}
            onSetActiveAudience={setActiveAudience}
            onSetAudienceToDelete={setAudienceToDelete}
            onOpenCustomAudience={openCustomAudienceDialog}
            onCancelSelection={resetAudienceSelection}
            onSaveSelection={handleAudienceSave}
          />
        )}
        {!!selectedAudienceMembers && (
          <AudienceMembersDetailsDialog
            audienceMembers={selectedAudienceMembers}
            onCancel={() => setSelectedAudienceMembers(null)}
          />
        )}
        {isCustomAudienceDialogOpen && (
          <CustomAudienceSetupDialog
            customGroupsCount={projectAudiences?.customGroups?.length}
            projectId={projectId}
            availableProfiles={projectProfiles}
            onCancel={closeCustomAudienceDialog}
          />
        )}
        {!!audienceToDelete && (
          <DeleteAudienceDialog
            projectId={projectId}
            availableProfiles={projectAudiences}
            audienceToDelete={audienceToDelete}
            activeAudience={activeAudience}
            onSetActiveAudience={setActiveAudience}
            onSetLatestPostAudience={onSetLatestPostAudience}
            onCancel={() => setAudienceToDelete(null)}
          />
        )}
      </>
    );
  }
);
